/**
 * Auth Store
 *
 * Manages authentication state including:
 * - Current user info
 * - JWT token
 * - Login/logout/refresh operations
 *
 * Uses localStorage for token persistence across sessions.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, UserRole, LoginRequest } from '../types/auth'
import { authService } from '../services/auth.service'

const TOKEN_KEY = 'claimshield_token'
const VIEW_AS_KEY = 'claimshield_view_as'

export const useAuthStore = defineStore('auth', () => {
  // ===== State =====
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const initialized = ref(false)
  const loading = ref(false)

  // View As feature - allows admins to see the UI as another user
  const viewingAs = ref<User | null>(null)

  // ===== Getters =====

  // The effective user for UI purposes (viewingAs takes precedence)
  const effectiveUser = computed(() => viewingAs.value || user.value)

  // True admin status (never affected by viewingAs)
  const isTrueAdmin = computed(() => user.value?.role === 'admin')

  // Whether currently viewing as another user
  const isViewingAs = computed(() => !!viewingAs.value)

  const isAuthenticated = computed(() => !!token.value && !!user.value)

  // Role checks use effectiveUser so "View As" works
  const isAdmin = computed(() => effectiveUser.value?.role === 'admin')

  const isStaff = computed(() => effectiveUser.value?.role === 'staff' || effectiveUser.value?.role === 'admin')

  const isClient = computed(() => effectiveUser.value?.role === 'client')

  const userDisplayName = computed(() => {
    const u = effectiveUser.value
    if (!u) return ''
    if (u.first_name || u.last_name) {
      return [u.first_name, u.last_name].filter(Boolean).join(' ')
    }
    return u.email
  })

  const userInitials = computed(() => {
    const u = effectiveUser.value
    if (!u) return '??'
    if (u.first_name && u.last_name) {
      return `${u.first_name[0]}${u.last_name[0]}`.toUpperCase()
    }
    return u.email.substring(0, 2).toUpperCase()
  })

  const clientName = computed(() => effectiveUser.value?.client_name || null)

  // True user display name (never affected by viewingAs)
  const trueUserDisplayName = computed(() => {
    if (!user.value) return ''
    if (user.value.first_name || user.value.last_name) {
      return [user.value.first_name, user.value.last_name].filter(Boolean).join(' ')
    }
    return user.value.email
  })

  // ===== Actions =====

  /**
   * Initialize auth state from stored token.
   * Called on app mount.
   *
   * Embed bootstrap: if the URL carries a `?t=<jwt>` query param (set by the
   * Zoho CRM proposal-dashboard widget), prefer that token over anything in
   * localStorage. This lets the iframe inherit a fresh session without ever
   * prompting Prosser to log in. The token is then written to localStorage so
   * subsequent route navigations inside the iframe don't need to re-pass it.
   */
  async function initialize(): Promise<void> {
    if (initialized.value) return

    loading.value = true

    try {
      // Embed bootstrap — pull token out of URL if present (CRM widget hand-off)
      let embedToken: string | null = null
      try {
        const params = new URLSearchParams(window.location.search)
        embedToken = params.get('t')
        if (embedToken) {
          localStorage.setItem(TOKEN_KEY, embedToken)
          // Strip the token from the URL so it doesn't sit in the address bar /
          // referer / history. Keep other query params (e.g. `embed=1`).
          params.delete('t')
          const remaining = params.toString()
          const newUrl =
            window.location.pathname +
            (remaining ? `?${remaining}` : '') +
            window.location.hash
          window.history.replaceState({}, '', newUrl)
        }
      } catch (e) {
        console.warn('[Auth] embed-bootstrap failed (non-fatal):', e)
      }

      // Try to restore token from storage (now includes the embed token if any)
      const storedToken = localStorage.getItem(TOKEN_KEY)

      if (storedToken) {
        // Validate token by fetching current user
        token.value = storedToken

        try {
          const currentUser = await authService.getCurrentUser()
          user.value = currentUser
          console.log('[Auth] Session restored for:', currentUser.email)

          // Restore View As if admin
          if (currentUser.role === 'admin') {
            restoreViewingAs()
          }
        } catch (error) {
          // Token invalid or expired
          console.log('[Auth] Stored token invalid, clearing')
          clearAuth()
        }
      }
    } finally {
      initialized.value = true
      loading.value = false
    }
  }

  /**
   * Login with email and password
   */
  async function login(credentials: LoginRequest): Promise<void> {
    loading.value = true

    try {
      // Get token from API
      const response = await authService.login(credentials)
      token.value = response.access_token

      // Persist token
      localStorage.setItem(TOKEN_KEY, response.access_token)

      // Fetch user info
      const currentUser = await authService.getCurrentUser()
      user.value = currentUser

      console.log('[Auth] Login successful:', currentUser.email)
    } catch (error) {
      clearAuth()
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Logout and clear all auth state
   */
  async function logout(): Promise<void> {
    try {
      // Optional: notify backend of logout
      // await authService.logout()
    } finally {
      clearAuth()
      console.log('[Auth] Logged out')
    }
  }

  /**
   * Clear auth state and storage
   */
  function clearAuth(): void {
    user.value = null
    token.value = null
    viewingAs.value = null
    localStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(VIEW_AS_KEY)
  }

  /**
   * Check if user has a specific role (uses effectiveUser for View As)
   */
  function hasRole(role: UserRole): boolean {
    const u = effectiveUser.value
    if (!u) return false

    // Admin has access to everything
    if (u.role === 'admin') return true

    // Staff has access to staff and client
    if (u.role === 'staff' && (role === 'staff' || role === 'client')) return true

    // Client only has client role
    return u.role === role
  }

  /**
   * Set viewing as another user (admin only)
   */
  function setViewingAs(targetUser: User): void {
    if (!isTrueAdmin.value) {
      console.warn('[Auth] Only admins can use View As feature')
      return
    }
    viewingAs.value = targetUser
    sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify(targetUser))
    console.log('[Auth] Now viewing as:', targetUser.email)
  }

  /**
   * Clear viewing as and return to own view
   */
  function clearViewingAs(): void {
    viewingAs.value = null
    sessionStorage.removeItem(VIEW_AS_KEY)
    console.log('[Auth] Cleared View As, back to own view')
  }

  /**
   * Restore viewingAs from session storage (called during initialize)
   */
  function restoreViewingAs(): void {
    if (!isTrueAdmin.value) return

    const stored = sessionStorage.getItem(VIEW_AS_KEY)
    if (stored) {
      try {
        viewingAs.value = JSON.parse(stored)
        console.log('[Auth] Restored View As:', viewingAs.value?.email)
      } catch {
        sessionStorage.removeItem(VIEW_AS_KEY)
      }
    }
  }

  /**
   * Check if user has any of the specified roles
   */
  function hasAnyRole(roles: UserRole[]): boolean {
    return roles.some((role) => hasRole(role))
  }

  /**
   * Update user profile locally (after API update)
   */
  function updateUserProfile(updates: Partial<User>): void {
    if (user.value) {
      user.value = { ...user.value, ...updates }
    }
  }

  return {
    // State
    user,
    token,
    initialized,
    loading,
    viewingAs,

    // Getters
    isAuthenticated,
    isAdmin,
    isStaff,
    isClient,
    isTrueAdmin,
    isViewingAs,
    effectiveUser,
    userDisplayName,
    userInitials,
    clientName,
    trueUserDisplayName,

    // Actions
    initialize,
    login,
    logout,
    clearAuth,
    hasRole,
    hasAnyRole,
    updateUserProfile,
    setViewingAs,
    clearViewingAs,
  }
})
